import { useRef, useState } from "react";

const useSymbol = () => {
  const [isOpen, setIsOpen] = useState(false);
  const savedRange = useRef<Range | null>(null);
  const savedFormState = useRef<{
    element: HTMLTextAreaElement | HTMLInputElement;
    start: number;
    end: number;
  } | null>(null);

  const saveSelection = () => {
    const activeEl = document.activeElement;
    if (
      activeEl &&
      (activeEl.tagName === "TEXTAREA" ||
        (activeEl.tagName === "INPUT" &&
          (activeEl as HTMLInputElement).type === "text"))
    ) {
      const el = activeEl as HTMLTextAreaElement | HTMLInputElement;
      savedFormState.current = {
        element: el,
        start: el.selectionStart || 0,
        end: el.selectionEnd || 0,
      };
      savedRange.current = null;
      return;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedRange.current = selection.getRangeAt(0);
      savedFormState.current = null;
    }
  };

  const isRangeInAllowedArea = (rng: Range | null) => {
    if (!rng) return false;
    const container = rng.commonAncestorContainer;
    const allowedSelectors = [
      ".scratch-pad",
      ".word-processor",
      ".spreadsheet-view",
      "input",
      "textarea",
      '[contenteditable="true"]',
    ];

    let currentNode: Node | null = container;
    while (currentNode && currentNode !== document.body) {
      if (currentNode.nodeType === 1) {
        const element = currentNode as Element;
        if (
          allowedSelectors.some(
            (selector) => element.matches && element.matches(selector)
          )
        ) {
          return true;
        }
      }
      currentNode = currentNode.parentNode;
    }
    return false;
  };

  const insertSymbol = (symbol: string) => {
    const activeEl = document.activeElement;
    const isFormEl =
      activeEl &&
      (activeEl.tagName === "TEXTAREA" || activeEl.tagName === "INPUT");

    // 1. Handle Form Elements (Special case for Scratchpad etc.)
    if (isFormEl || savedFormState.current) {
      const el = (isFormEl ? activeEl : savedFormState.current?.element) as
        | HTMLTextAreaElement
        | HTMLInputElement;
      const start = isFormEl
        ? el.selectionStart || 0
        : savedFormState.current!.start;
      const end = isFormEl ? el.selectionEnd || 0 : savedFormState.current!.end;

      const val = el.value;
      el.value = val.slice(0, start) + symbol + val.slice(end);

      // Update cursor position
      el.focus();
      const newPos = start + symbol.length;
      el.setSelectionRange(newPos, newPos);

      // Trigger change event for React to pick up
      const event = new Event("input", { bubbles: true });
      el.dispatchEvent(event);

      savedFormState.current = { element: el, start: newPos, end: newPos };
      return;
    }

    // 2. Handle Rich Text / ContentEditable
    const selection = window.getSelection();
    let range: Range | null = null;

    if (selection && selection.rangeCount > 0) {
      const liveRange = selection.getRangeAt(0);
      if (isRangeInAllowedArea(liveRange)) {
        range = liveRange;
      }
    }

    if (!range && savedRange.current) {
      if (isRangeInAllowedArea(savedRange.current)) {
        range = savedRange.current;
      }
    }

    if (!range) return;

    range.deleteContents();
    const symbolNode = document.createTextNode(symbol);
    range.insertNode(symbolNode);
    range.setStartAfter(symbolNode);
    range.setEndAfter(symbolNode);
    selection?.removeAllRanges();
    selection?.addRange(range);
    savedRange.current = range;
  };

  return {
    isOpen,
    setIsOpen,
    insertSymbol,
    saveSelection,
  };
};

export default useSymbol;
